import React from 'react';
import moment from 'moment';
import { CheckCircle, AlertCircle, X } from 'react-feather';
import { DillButton } from '../Dill/DillButton';
import { DillModal } from '../Dill/DillModal';

export interface StatementSendResult {
  sentAt: string;
  statementId: string | null;
  sentCount: number;
  failedBuyers: Array<{ id: string; name: string }>;
}

interface AppStatementSendResultsModalProps {
  open: boolean;
  result: StatementSendResult | null;
  onClose: () => void;
}

const AppStatementSendResultsModal: React.FC<AppStatementSendResultsModalProps> = ({
  open,
  result,
  onClose,
}) => {
  if (!result) return null;

  const { sentCount, failedBuyers = [], statementId, sentAt } = result;
  const hasFailures = failedBuyers.length > 0;
  const allFailed = sentCount === 0 && hasFailures;

  const sentLabel = statementId
    ? moment(statementId, 'YYYY-MM').format('MMMM YYYY') + ' snapshot'
    : 'live statement';

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title="Statement Send Results"
      maxWidth="min(90vw, 480px)"
      footer={
        <DillButton
          label="Close"
          onClick={onClose}
          variant={hasFailures ? 'secondary' : 'primary'}
        />
      }>
      <div className="space-y-4">
        {/* Sent at */}
        <p className="text-xs text-gray-400">
          Sent {moment(sentAt).format('MMM D, YYYY [at] h:mm A')} · {sentLabel}
        </p>

        {/* Success banner */}
        {!allFailed && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-50 border border-green-200">
            <CheckCircle size={18} className="text-green-500 shrink-0" />
            <p className="text-sm font-medium text-green-800">
              {sentCount} statement{sentCount !== 1 ? 's' : ''} sent successfully
            </p>
          </div>
        )}

        {/* Failed buyers */}
        {hasFailures && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <AlertCircle size={14} className="text-amber-500 shrink-0" />
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                {failedBuyers.length} customer{failedBuyers.length !== 1 ? 's' : ''} did not receive
                a statement
              </p>
            </div>
            <div className="flex flex-col gap-1 max-h-52 overflow-y-auto">
              {failedBuyers.map((buyer) => (
                <div
                  key={buyer.id}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-100">
                  <span className="text-sm text-gray-700">{buyer.name}</span>
                  <X size={12} className="text-amber-400 shrink-0" />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400">
              Check that these customers have valid email addresses in your accounting system.
            </p>
          </div>
        )}

        {/* All failed */}
        {allFailed && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200">
            <AlertCircle size={18} className="text-red-500 shrink-0" />
            <p className="text-sm font-medium text-red-800">No statements were sent successfully</p>
          </div>
        )}
      </div>
    </DillModal>
  );
};

export default AppStatementSendResultsModal;
